import React, {useEffect} from "react";
import {
  Typography,
  Box,
  Button,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  Avatar,
} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {
  Visibility as VisibilityIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import {useClientQueries, useServiceRequestQueries, useNotification, formatDate} from "shared-lib";

const ServiceRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const {clientId} = useParams<{clientId: string}>();
  const {showNotification} = useNotification();

  const {useClientById} = useClientQueries();
  const {useServiceRequestsByClient} = useServiceRequestQueries();

  const {data: client, isLoading: clientLoading, error: clientError} = useClientById(clientId || "");

  const {
    data: serviceRequests,
    isLoading: requestsLoading,
    error: requestsError,
  } = useServiceRequestsByClient(clientId || "");

  const isLoading = clientLoading || requestsLoading;
  const error = clientError || requestsError;

  // Show error notification if there's an error
  useEffect(() => {
    if (error) {
      showNotification({
        message: "Error loading data. Please try again.",
        severity: "error",
      });
    }
  }, [error, showNotification]);

  const handleNewRequest = () => {
    navigate(`/clients/${clientId}/requests/new`);
  };

  const handleViewRequest = (requestId: string) => {
    navigate(`/clients/${clientId}/requests/${requestId}`);
  };

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case "active":
        return "info";
      case "closed":
        return "success";
      case "removed":
        return "error";
      default:
        return "default";
    }
  };

  if (isLoading) {
    return (
      <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh"}}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box sx={{my: 4}}>
        <Paper sx={{p: 3, borderRadius: "16px", mb: 2}}>
          <Typography variant="h5" color="error">
            Error Loading Data
          </Typography>
          <Typography variant="body1" sx={{mt: 2}}>
            There was an error loading the service requests. Please try again later.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{mb: 2}}>
      {serviceRequests && serviceRequests.length > 0 ? (
        <Grid container spacing={2}>
          {serviceRequests.map(request => (
            <Grid item xs={12} key={request.id}>
              <Card
                sx={{
                  borderRadius: "12px",
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s ease",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  "&:hover": {
                    boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
                    transform: "translateY(-2px)",
                  },
                }}>
                <CardContent sx={{p: 3}}>
                  <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2}}>
                    <Box sx={{display: "flex", alignItems: "flex-start"}}>
                      <Avatar
                        sx={{
                          bgcolor: "primary.light",
                          mr: 2,
                          width: 48,
                          height: 48,
                          display: {xs: "none", sm: "flex"},
                        }}>
                        {(request.serviceName || request.serviceType || "?").charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {request.serviceName || request.serviceType}
                        </Typography>
                        <Box sx={{display: "flex", alignItems: "center", mt: 0.5}}>
                          <LocationIcon sx={{fontSize: 16, mr: 0.5, color: "text.secondary"}} />
                          <Typography variant="body2" color="text.secondary">
                            {request.city}, {request.state}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Chip
                      label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      color={getStatusChipColor(request.status) as any}
                      size="small"
                      sx={{fontWeight: 500, px: 1}}
                    />
                  </Box>

                  <Divider sx={{my: 2}} />

                  <Grid container spacing={2} sx={{mt: 1}}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{display: "flex", alignItems: "center"}}>
                        <CalendarIcon fontSize="small" sx={{mr: 1, color: "primary.main"}} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Created
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDate(request.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{display: "flex", alignItems: "center"}}>
                        <TimeIcon fontSize="small" sx={{mr: 1, color: "primary.main"}} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Timeline
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {request.projectTimeline}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{display: "flex", alignItems: "center"}}>
                        <CategoryIcon fontSize="small" sx={{mr: 1, color: "primary.main"}} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Bids
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {request.bidCount || 0}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sm={3}
                      sx={{display: "flex", justifyContent: {xs: "flex-start", sm: "flex-end"}, alignItems: "center"}}>
                      <Button
                        variant="outlined"
                        size="medium"
                        endIcon={<VisibilityIcon />}
                        onClick={() => handleViewRequest(request.id)}
                        sx={{
                          borderRadius: "8px",
                          fontWeight: 500,
                          "&:hover": {
                            backgroundColor: "primary.main",
                            color: "white",
                          },
                        }}>
                        View Details
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          sx={{
            p: 5,
            textAlign: "center",
            borderRadius: "16px",
            border: "1px dashed rgba(0, 0, 0, 0.12)",
            backgroundColor: "rgba(0, 0, 0, 0.01)",
          }}>
          <Typography variant="h6" sx={{mb: 2, fontWeight: 500}}>
            No Service Requests
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
            You haven't created any service requests yet.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewRequest}
            color="primary"
            sx={{borderRadius: "8px"}}>
            Create Your First Request
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default ServiceRequestsPage;
